import { useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { COR_PRIMARIA_PDF } from '@/nucleo/pdf/cores';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { DateRangePresetFilter } from '@/ui/DateRangePresetFilter';
import { resolveDateRange, type DateRangeValue } from '@/ui/dateRangePreset';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { GlossaryTerm } from '@/ui/GlossaryTerm';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { PageHeader } from '@/ui/PageHeader';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { StatusBadge, type StatusVariant } from '@/ui/StatusBadge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/base/tooltip';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Eye,
  FileSignature,
  FileText,
  Send,
  Settings2,
  XCircle,
} from 'lucide-react';

import { useDemonstrativos } from './hooks/useDemonstrativos';
import type { DemonstrativoLinha, StatusDemonstrativoCredenciado } from './types';

function opcoesMes(): { value: string; label: string }[] {
  const hoje = new Date();
  return Array.from({ length: 12 }, (_, i) => {
    const data = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    const competencia = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
    const rotulo = data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return { value: competencia, label: rotulo.charAt(0).toUpperCase() + rotulo.slice(1) };
  });
}

const MESES = opcoesMes();

const ROTULO_STATUS: Record<
  StatusDemonstrativoCredenciado,
  { label: string; variant: StatusVariant }
> = {
  GERADO: { label: 'Gerado', variant: 'rascunho' },
  ENVIADO: { label: 'Enviado', variant: 'pendente' },
  VISUALIZADO: { label: 'Visualizado', variant: 'aprovado' },
  RECUSADO: { label: 'Recusado', variant: 'rejeitado' },
};

function formatarData(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(iso));
}

/** Uma folha PDF por credenciado, com o que já temos no mock (sem o
 * detalhamento por item/OS do protótipo — depende do motor de cálculo, que
 * ainda não existe). */
function exportarDemonstrativoPdf(linha: DemonstrativoLinha) {
  const documento = new jsPDF();
  documento.setFontSize(14);
  documento.text('Demonstrativo financeiro', 14, 16);
  documento.setFontSize(10);
  documento.text(`Credenciado: ${linha.credenciadoNome}`, 14, 26);
  documento.text(`CPF/CNPJ: ${linha.cpfCnpj}`, 14, 32);
  documento.text(
    `Competência: ${new Date(`${linha.mesReferencia}T00:00:00`).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`,
    14,
    38,
  );

  autoTable(documento, {
    startY: 46,
    head: [['Ajustes', 'Soma dos ajustes', 'Valor total']],
    body: [
      [
        String(linha.ajustesQuantidade),
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
          parseFloat(linha.ajustesSoma),
        ),
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
          parseFloat(linha.valorTotal),
        ),
      ],
    ],
    styles: { fontSize: 9 },
    headStyles: { fillColor: COR_PRIMARIA_PDF },
  });

  documento.save(`demonstrativo-${linha.credenciadoId}-${linha.mesReferencia.slice(0, 7)}.pdf`);
}

/**
 * Demonstrativos financeiros por competência — standalone
 * (`/operacao/financeiro/demonstrativos`) e embutida na aba "Demonstrativos"
 * do Fechamento Mensal V2.
 */
export function DemonstrativosPage({
  embedded = false,
  mesReferencia,
}: {
  embedded?: boolean;
  mesReferencia?: string;
}) {
  const [busca, definirBusca] = useState('');
  const [mes, definirMes] = useState(mesReferencia ?? '');
  const [tipoPessoa, definirTipoPessoa] = useState<'PF' | 'PJ' | 'TODOS'>('TODOS');
  const [periodo, definirPeriodo] = useState<DateRangeValue>({ preset: 'THIS_MONTH' });
  const [ajustesAberto, definirAjustesAberto] = useState<DemonstrativoLinha | null>(null);
  const [glosaAberta, definirGlosaAberta] = useState<DemonstrativoLinha | null>(null);

  const { data, isLoading, isError, refetch } = useDemonstrativos({
    busca: busca || undefined,
    mes: mes || undefined,
    tipoPessoa,
  });
  // Enviar os demonstrativos ainda não tem endpoint: mostra o aviso de
  // indisponibilidade e não muda nada (CLAUDE.md, exceção do Fechamento Mensal,
  // item 2). Não existe gancho de escrita para isso, de propósito.

  const usandoMes = !!mes;
  const linhasBase = useMemo(() => {
    const base = data ?? [];
    if (usandoMes) return base;
    const { from, to } = resolveDateRange(periodo);
    if (!from && !to) return base;
    return base.filter((d) => {
      const referencia = d.mesReferencia.slice(0, 10);
      if (from && referencia < from) return false;
      if (to && referencia > to) return false;
      return true;
    });
  }, [data, usandoMes, periodo]);

  const escopoLabel = usandoMes
    ? (MESES.find((m) => m.value === mes)?.label ?? mes)
    : 'período selecionado';
  const valorTotalEscopo = linhasBase.reduce((s, l) => s + parseFloat(l.valorTotal), 0);

  const pendentes = linhasBase.filter((d) => d.statusCredenciado === 'GERADO');
  const enviados = linhasBase.filter((d) => d.statusCredenciado !== 'GERADO');
  const visualizados = linhasBase.filter((d) => d.statusCredenciado === 'VISUALIZADO').length;
  const concordaram = linhasBase.filter((d) => d.concordanciaEm).length;
  const recusados = linhasBase.filter((d) => d.statusCredenciado === 'RECUSADO').length;

  const colunas: DataTableColumn<DemonstrativoLinha & { id: string }>[] = [
    {
      header: 'Credenciado',
      cell: (l) => (
        <span className="flex items-center gap-1">
          {l.devolvida && (
            <AlertTriangle className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />
          )}
          {l.credenciadoNome}
        </span>
      ),
    },
    { header: 'CPF/CNPJ', accessorKey: 'cpfCnpj', cellClassName: 'font-mono text-xs' },
    { header: 'Tipo', cell: (l) => <Badge variant="outline">{l.tipoPessoa}</Badge> },
    {
      header: 'Mês',
      cell: (l) =>
        new Date(`${l.mesReferencia}T00:00:00`).toLocaleDateString('pt-BR', {
          month: '2-digit',
          year: 'numeric',
        }),
    },
    {
      header: 'Ajustes',
      tooltip: 'Glosas, multas avulsas e bônus aplicados antes da exportação SOF.',
      cell: (l) =>
        l.ajustesQuantidade > 0 ? (
          <span>
            {l.ajustesQuantidade} · <MoneyDisplay value={l.ajustesSoma} />
          </span>
        ) : (
          '—'
        ),
    },
    {
      header: 'Status com o credenciado',
      cell: (l) => (
        <div className="flex flex-col gap-1">
          <StatusBadge
            status={ROTULO_STATUS[l.statusCredenciado].variant}
            label={ROTULO_STATUS[l.statusCredenciado].label}
          />
          {l.concordanciaEm && (
            <span className="inline-flex items-center gap-1 text-xs text-success">
              <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> Concordou em{' '}
              {formatarData(l.concordanciaEm)}
            </span>
          )}
          {l.statusCredenciado === 'RECUSADO' && l.motivoRecusa && (
            <span
              className="max-w-[220px] truncate text-xs text-destructive"
              title={l.motivoRecusa}
            >
              {l.motivoRecusa}
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'Valor total',
      cell: (l) => <MoneyDisplay value={l.valorTotal} />,
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Ações',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (l) => (
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => definirAjustesAberto(l)}>
            <Settings2 className="mr-1 h-4 w-4" aria-hidden="true" /> Ajustes
          </Button>
          {l.statusCredenciado !== 'RECUSADO' && (
            <Button variant="outline" size="sm" onClick={() => definirGlosaAberta(l)}>
              <AlertTriangle className="mr-1 h-4 w-4" aria-hidden="true" /> Glosa formal
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => exportarDemonstrativoPdf(l)}>
            <Download className="mr-1 h-4 w-4" aria-hidden="true" /> PDF
          </Button>
        </div>
      ),
    },
  ];

  const linhas = linhasBase.map((l) => ({ ...l, id: l.credenciadoId }));

  const campos: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por credenciado…',
      ariaLabel: 'Buscar credenciado',
      flex: true,
    },
    ...(!mesReferencia
      ? ([
          {
            key: 'mes',
            label: 'Mês de referência',
            type: 'select',
            allOptionLabel: 'Todos os meses',
            width: 'w-full sm:w-[220px]',
            options: MESES,
          },
        ] as FilterFieldConfig[])
      : []),
    {
      key: 'tipoPessoa',
      label: 'Tipo pessoa',
      type: 'select',
      showAllOption: false,
      width: 'w-full sm:w-[180px]',
      options: [
        { value: 'TODOS', label: 'Todos' },
        { value: 'PF', label: 'Pessoa Física' },
        { value: 'PJ', label: 'Pessoa Jurídica' },
      ],
    },
  ];

  const subtitulo = (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      <span>
        Demonstrativos PF/PJ em PDF e ajustes (glosas, multas avulsas, bônus) por{' '}
        <GlossaryTerm termo="MEDICAO" esconderIcone>
          medição
        </GlossaryTerm>
        .
      </span>
      <span className="text-muted-foreground">·</span>
      <span>
        <strong className="tabular-nums text-foreground">{linhasBase.length}</strong> em{' '}
        {escopoLabel}
      </span>
      {valorTotalEscopo > 0 && (
        <>
          <span className="text-muted-foreground">·</span>
          <span className="tabular-nums">
            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
              valorTotalEscopo,
            )}
          </span>
        </>
      )}
    </span>
  );

  return (
    <div className="space-y-6">
      {!embedded && <PageHeader title="Demonstrativos" description={subtitulo} />}

      <FilterBar
        fields={campos}
        values={{ busca, mes: mes || 'ALL', tipoPessoa }}
        onChange={(chave, valor) => {
          if (chave === 'busca') definirBusca(valor);
          else if (chave === 'mes') definirMes(valor === 'ALL' ? '' : valor);
          else definirTipoPessoa((valor || 'TODOS') as 'PF' | 'PJ' | 'TODOS');
        }}
        hasActiveFilters={!!busca || !!mes || tipoPessoa !== 'TODOS'}
        onClear={() => {
          definirBusca('');
          definirMes(mesReferencia ?? '');
          definirTipoPessoa('TODOS');
          definirPeriodo({ preset: 'THIS_MONTH' });
        }}
        defaultValues={{ busca: '', mes: mesReferencia ?? 'ALL', tipoPessoa: 'TODOS' }}
      >
        {!mesReferencia && (
          <DateRangePresetFilter
            label="Período de competência"
            value={periodo}
            onChange={definirPeriodo}
            width="w-full sm:w-[200px]"
          />
        )}
      </FilterBar>

      {usandoMes && !mesReferencia && (
        <p className="-mt-2 px-1 text-xs text-muted-foreground">
          Mês específico selecionado — período ignorado.
        </p>
      )}

      <TooltipProvider delayDuration={200}>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-md border border-transparent bg-warning px-2.5 py-1 font-medium tabular-nums text-warning-foreground">
              <FileSignature className="h-3.5 w-3.5" aria-hidden="true" />
              {pendentes.length} pendente{pendentes.length === 1 ? '' : 's'} de envio
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/40 px-2.5 py-1 tabular-nums text-muted-foreground">
              <Send className="h-3.5 w-3.5" aria-hidden="true" />
              {enviados.length} enviado{enviados.length === 1 ? '' : 's'}
            </span>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:flex-nowrap sm:items-center sm:justify-end sm:gap-3">
            <div
              className="inline-flex w-full items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground sm:w-auto sm:justify-start"
              aria-label="Retorno dos credenciados"
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 tabular-nums">
                    <Eye className="h-3.5 w-3.5 text-warning" aria-hidden="true" />
                    <span className="font-medium text-foreground">{visualizados}</span>
                    <span className="hidden md:inline">visualizados</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Credenciado abriu o demonstrativo</TooltipContent>
              </Tooltip>
              <span className="h-3 w-px bg-border/80" aria-hidden="true" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 tabular-nums">
                    <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden="true" />
                    <span className="font-medium text-foreground">{concordaram}</span>
                    <span className="hidden md:inline">concordaram</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Credenciado aceitou e anexou NF/recibo</TooltipContent>
              </Tooltip>
              <span className="h-3 w-px bg-border/80" aria-hidden="true" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 tabular-nums">
                    <XCircle className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />
                    <span className="font-medium text-foreground">{recusados}</span>
                    <span className="hidden md:inline">recusados</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Credenciado recusou — requer reabertura do cálculo</TooltipContent>
              </Tooltip>
            </div>

            {pendentes.length > 0 && (
              <Button
                size="sm"
                onClick={() => mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL)}
                className="w-full shrink-0 gap-2 sm:w-auto"
              >
                <Send className="h-3.5 w-3.5" aria-hidden="true" />
                Enviar todos os pendentes ({pendentes.length})
              </Button>
            )}
          </div>
        </div>
      </TooltipProvider>

      <p className="px-1 text-xs text-muted-foreground">
        Demonstrativos gerados a partir do fechamento selado. Se algo estiver errado, corrija na
        origem (Ateste / Conciliação) e gere um novo demonstrativo — não existe etapa intermediária
        de rascunho.
      </p>

      {isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
        />
      ) : (
        <DataTable
          columns={colunas}
          data={linhas}
          isLoading={isLoading}
          emptyIcon={FileText}
          emptyTitle="Nada por aqui"
          emptyDescription="Nenhuma medição corresponde aos filtros aplicados."
          ariaLabel="Demonstrativos financeiros"
        />
      )}

      <div className="flex flex-wrap items-center gap-3 px-1 text-xs text-muted-foreground">
        <span className="font-medium text-foreground/80">Legenda:</span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="h-3 w-3 rounded border border-destructive/40 bg-destructive/20"
            aria-hidden="true"
          />
          Devolvida / rejeitada
        </span>
        <span className="inline-flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3 text-destructive" aria-hidden="true" />
          Requer reapresentação
        </span>
      </div>

      <ResponsiveModal
        open={!!ajustesAberto}
        onOpenChange={(aberto) => !aberto && definirAjustesAberto(null)}
        title={`Ajustes — ${ajustesAberto?.credenciadoNome ?? ''}`}
        description="Glosas, multas avulsas e bônus aplicados antes da exportação SOF."
      >
        <div className="space-y-3">
          {ajustesAberto && ajustesAberto.ajustesQuantidade > 0 ? (
            <div className="flex items-center justify-between rounded-md border p-3 text-sm">
              <span>{ajustesAberto.ajustesQuantidade} ajuste(s) lançado(s) nesta medição</span>
              <MoneyDisplay value={ajustesAberto.ajustesSoma} className="font-medium" />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhum ajuste registrado nesta medição.</p>
          )}
          <p className="text-xs text-muted-foreground">
            Registrar um novo ajuste (glosa, multa avulsa ou bônus) depende do motor de cálculo, que
            ainda não existe — entra numa próxima etapa da migração.
          </p>
        </div>
      </ResponsiveModal>

      <ResponsiveModal
        open={!!glosaAberta}
        onOpenChange={(aberto) => !aberto && definirGlosaAberta(null)}
        title={`Aplicar glosa formal — ${glosaAberta?.credenciadoNome ?? ''}`}
        description="Vai para auditoria — depende do catálogo de tipos de glosa, que ainda não existe."
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="glosa-tipo">Tipo de glosa</Label>
            <Input
              id="glosa-tipo"
              disabled
              placeholder="Catálogo de tipos de glosa — próxima etapa"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => definirGlosaAberta(null)}>
              Fechar
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
}

export default DemonstrativosPage;
